import React, { useState } from 'react';
import { MessageCircle, Hand, ArrowRightLeft, Building2, Loader2, Pause, StopCircle } from 'lucide-react';

/**
 * Icon and style for a given pause reason code
 */
function getReasonMeta(pauseReason) {
  switch (pauseReason) {
    case 'lead_replied':
      return {
        icon: <MessageCircle className="w-3 h-3" />,
        className: 'bg-orange-50 text-orange-700 border border-orange-100',
      };
    case 'status_change':
      return {
        icon: <ArrowRightLeft className="w-3 h-3" />,
        className: 'bg-blue-50 text-blue-700 border border-blue-100',
      };
    case 'no_interested_property':
      return {
        icon: <Building2 className="w-3 h-3" />,
        className: 'bg-amber-50 text-amber-700 border border-amber-100',
      };
    case 'manual':
    case 'automation_stopped':
      return {
        icon: <Hand className="w-3 h-3" />,
        className: 'bg-stone-100 text-stone-600 border border-stone-200',
      };
    default:
      return {
        icon: null,
        className: 'bg-stone-100 text-stone-600 border border-stone-200',
      };
  }
}

/**
 * PausedLeadRow — displays a single paused/rejected execution.
 *
 * Expected `lead` shape (normalized in AutomationsContainer):
 *   { id, leadId, automationId, name, email, workflow, step, reason, pauseReason, time, status }
 *
 * `onResume(runId)` — calls resumeRun cloud function
 *
 * Resume logic:
 *  - status === 'paused'   → Resume enabled (workflow can continue from current step)
 *  - status === 'rejected' → Resume disabled (permanently stopped; must re-add to automation)
 */
const PausedLeadRow = ({ lead, isMobile, onResume }) => {
  const [resuming, setResuming] = useState(false);

  // Determine if this execution can be resumed
  const isStopped = (lead.status || '').toLowerCase() === 'rejected';
  const isPaused = (lead.status || '').toLowerCase() === 'paused';
  const canResume = isPaused && !isStopped;

  const handleResume = async () => {
    if (!onResume || resuming || !canResume) return;
    setResuming(true);
    try {
      await onResume(lead.id);
    } catch {
      // error is logged in the handler
    } finally {
      setResuming(false);
    }
  };

  const { icon, className: badgeClass } = getReasonMeta(lead.pauseReason);

  if (isMobile) {
    return (
      <div className="p-4 flex flex-col gap-2">
        <div className="flex justify-between items-start">
          <div>
            <div className="flex items-center gap-2">
              <div className="font-medium text-stone-700">{lead.fullName || lead.name}</div>
              {/* Status badge: Paused (amber) vs Stopped (red) */}
              {isStopped ? (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-semibold bg-red-50 text-red-600 border border-red-100">
                  <StopCircle className="w-2.5 h-2.5" /> Stopped
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-semibold bg-amber-50 text-amber-600 border border-amber-100">
                  <Pause className="w-2.5 h-2.5" /> Paused
                </span>
              )}
            </div>
            <div className="text-[10px] text-stone-400">{lead.email}</div>
          </div>
          <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full font-medium text-[10px] ${badgeClass}`}>
            {icon}
            {lead.reason}
          </span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-[10px] text-stone-500">{lead.workflow} • Step: {lead.step}</span>
          <span className="text-[10px] text-stone-400">{lead.time}</span>
        </div>
        <div className="flex gap-2 mt-1">
          {canResume ? (
            <button
              onClick={handleResume}
              disabled={resuming}
              className="flex-1 text-white text-xs bg-black py-2 rounded-lg font-medium disabled:opacity-50 flex items-center justify-center gap-1.5"
            >
              {resuming ? <><Loader2 className="w-3 h-3 animate-spin" /> Resuming…</> : 'Resume'}
            </button>
          ) : (
            <div className="flex-1 text-center text-[10px] text-stone-400 py-2">
              Permanently stopped — re-add lead to restart
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <tr className="hover:bg-notion-sidebar transition-colors">
      <td className="p-4">
        <div className="flex items-center gap-2">
          <div className="font-medium text-stone-700">{lead.fullName || lead.name}</div>
          {/* Status badge: Paused (amber) vs Stopped (red) */}
          {isStopped ? (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-semibold bg-red-50 text-red-600 border border-red-100">
              <StopCircle className="w-2.5 h-2.5" /> Stopped
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-semibold bg-amber-50 text-amber-600 border border-amber-100">
              <Pause className="w-2.5 h-2.5" /> Paused
            </span>
          )}
        </div>
        <div className="text-[10px] text-stone-400">{lead.email}</div>
      </td>
      <td className="p-4">
        <div className="text-stone-600">{lead.workflow}</div>
        <div className="text-[10px] text-stone-400">{isStopped ? 'Stopped' : 'Paused'} at {lead.step}</div>
      </td>
      <td className="p-4">
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full font-medium ${badgeClass}`}>
          {icon}
          {lead.reason}
        </span>
      </td>
      <td className="p-4 text-stone-400">{lead.time}</td>
      <td className="p-4 text-right">
        {canResume ? (
          <button
            onClick={handleResume}
            disabled={resuming}
            className="text-white bg-black font-medium px-3 py-1.5 rounded hover:bg-stone-800 shadow-sm transition ml-2 disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            {resuming ? <><Loader2 className="w-3 h-3 animate-spin" /> Resuming…</> : 'Resume'}
          </button>
        ) : (
          <span className="text-[10px] text-stone-400 italic">Permanently stopped</span>
        )}
      </td>
    </tr>
  );
};

export default PausedLeadRow;
